import { useState, type CSSProperties, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useApp, Icon, IconButton, Avatar, Card, Button, NakedButton, Pill, Banner } from '@/components/ds';
import { Tracker } from '@/components/code';
import { asset } from '@/lib/assets';
import { ApiKeyGate, PreviewNote } from '@/components/live';
import { DASH, fmtMoney, statusKind, statusLabel } from '@/data';
import { BusinessesView, PaymentsView, FundingView, DeliveryView } from '@/containers/dashboard-businesses';
import { KeysView, LogsView, EventsView, WebhooksView } from '@/containers/dashboard-developers';
import type { Go } from '@/lib/types';

// dashboard.tsx — authenticated portal shell + Home + shared widgets (Drawer, Table, StatCard).

// ---- Drawer (right slide-over) ------------------------------------------
interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  width?: number;
  style?: CSSProperties;
}
function Drawer({ open, onClose, title, children, footer, width = 540 }: DrawerProps) {
  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(24,25,27,0.5)',
        zIndex: 1100,
        display: 'flex',
        justifyContent: 'flex-end',
        animation: 'om-fade .2s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: width,
          background: '#fff',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'var(--shadow-600)',
          animation: 'om-slide-in .28s cubic-bezier(0.2,0.8,0.2,1)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', padding: '18px 24px', borderBottom: '1px solid var(--border-static)' }}>
          <h2 style={{ margin: 0, fontSize: 19, fontWeight: 600, flex: 1 }}>{title}</h2>
          <IconButton name="close" onClick={onClose} ariaLabel="Close" />
        </div>
        <div style={{ padding: 24, overflow: 'auto', flex: 1 }}>{children}</div>
        {footer && (
          <div
            style={{
              padding: '16px 24px',
              borderTop: '1px solid var(--border-static)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 12,
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Table --------------------------------------------------------------
interface TableColumn {
  key: string;
  label: string;
  align?: 'left' | 'right' | 'center';
  render?: (row: any) => ReactNode;
}

interface TableProps {
  columns: TableColumn[];
  rows: any[];
  onRow?: (row: any) => void;
  style?: CSSProperties;
}
function Table({ columns, rows, onRow }: TableProps) {
  return (
    <div style={{ border: '1px solid var(--border-static)', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'var(--neutral-200)' }}>
            {columns.map((c) => (
              <th
                key={c.key}
                style={{
                  textAlign: c.align || 'left',
                  padding: '11px 16px',
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                  textTransform: 'uppercase',
                  letterSpacing: '.03em',
                  whiteSpace: 'nowrap',
                }}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={i}
              onClick={() => onRow && onRow(r)}
              className="om-row"
              style={{ borderTop: '1px solid var(--border-static)', cursor: onRow ? 'pointer' : 'default' }}
            >
              {columns.map((c) => (
                <td key={c.key} style={{ padding: '13px 16px', fontSize: 14, textAlign: c.align || 'left', verticalAlign: 'middle' }}>
                  {c.render ? c.render(r) : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface StatCardProps {
  label: string;
  value: ReactNode;
  sub?: string;
  icon?: string;
  accent?: boolean;
  style?: CSSProperties;
}
function StatCard({ label, value, sub, icon, accent }: StatCardProps) {
  return (
    <Card padding={20} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500 }}>{label}</span>
        <Icon name={icon ?? ''} size={18} color={accent ? 'var(--accent)' : 'var(--neutral-600)'} />
      </div>
      <div style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-0.01em' }}>{value}</div>
      {sub && <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{sub}</div>}
    </Card>
  );
}

interface PageTitleProps {
  title: ReactNode;
  sub?: string;
  action?: ReactNode;
  style?: CSSProperties;
}
function PageTitle({ title, sub, action }: PageTitleProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 24, gap: 16 }}>
      <div style={{ flex: 1 }}>
        <h1 style={{ margin: 0, fontSize: 26, letterSpacing: '-0.02em' }}>{title}</h1>
        {sub && <p style={{ fontSize: 14.5, color: 'var(--text-secondary)', margin: '6px 0 0' }}>{sub}</p>}
      </div>
      {action}
    </div>
  );
}

// ---- Shell --------------------------------------------------------------
const DASH_NAV: Array<{
  group: string | null;
  items: Array<[string, string, string]>;
}> = [
  {
    group: null,
    items: [
      ['businesses', 'Businesses', 'business'],
      ['payments', 'Payments', 'pay'],
      ['funding', 'Funding sources', 'bank'],
      ['delivery', 'Delivery methods', 'paper-check'],
    ],
  },
  {
    group: 'Developers',
    items: [
      ['keys', 'API keys', 'lock'],
      ['logs', 'Logs', 'list'],
      ['events', 'Events', 'notification'],
      ['webhooks', 'Webhooks', 'fast'],
    ],
  },
];

interface DashboardProps {
  go: Go;
  style?: CSSProperties;
}
function Dashboard({ go }: DashboardProps) {
  const app = useApp();
  const { view: viewParam } = useParams();
  const view = viewParam || 'payments';
  // View switches are real navigations so each dashboard section has its own URL.
  const setView = (id: string) => go(id === 'home' ? '/dashboard' : `/dashboard/${id}`);
  const [biz, setBiz] = useState(DASH.businesses[0]);
  const trusted = app.partner === 'trusted';

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: 'var(--neutral-200)' }}>
      {/* left rail */}
      <aside
        style={{
          width: 244,
          flexShrink: 0,
          background: '#fff',
          borderRight: '1px solid var(--border-static)',
          display: 'flex',
          flexDirection: 'column',
          padding: '16px 12px',
        }}
      >
        <button
          onClick={() => go('/')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            padding: '4px 8px 16px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          <img src={asset('melio-symbol.svg')} width={28} height={28} alt="" />
          <span style={{ fontSize: 15, fontWeight: 600 }}>API</span>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: 'var(--accent)',
              background: 'var(--accent-soft)',
              borderRadius: 5,
              padding: '2px 5px',
            }}
          >
            DEV
          </span>
        </button>
        {DASH_NAV.map((sec, si) => (
          <div key={si} style={{ marginBottom: 14 }}>
            {sec.group && (
              <div className="eyebrow" style={{ padding: '4px 10px', color: 'var(--text-secondary)' }}>
                {sec.group}
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {sec.items.map(([id, label, icon]) => {
                const active = view === id;
                return (
                  <button
                    key={id}
                    onClick={() => setView(id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 11,
                      padding: '8px 10px',
                      borderRadius: 8,
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      background: active ? 'var(--accent-soft)' : 'transparent',
                      color: active ? 'var(--accent-text)' : 'var(--text-primary)',
                      transition: 'background-color .12s',
                    }}
                  >
                    <Icon name={icon} size={18} color={active ? 'var(--accent)' : 'var(--icon-primary)'} />
                    <span style={{ fontSize: 14, fontWeight: active ? 600 : 500 }}>{label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <button
            onClick={() => go('/reference')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 11,
              padding: '8px 10px',
              borderRadius: 8,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: 'var(--text-secondary)',
            }}
          >
            <Icon name="document" size={18} color="var(--icon-primary)" />
            <span style={{ fontSize: 14, fontWeight: 500 }}>Documentation</span>
          </button>
          <button
            onClick={() => go('/')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 11,
              padding: '8px 10px',
              borderRadius: 8,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: 'var(--text-secondary)',
            }}
          >
            <Icon name="arrow-left" size={18} color="var(--icon-primary)" />
            <span style={{ fontSize: 14, fontWeight: 500 }}>Back to site</span>
          </button>
        </div>
      </aside>

      {/* main */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* top bar */}
        <header
          style={{
            height: 64,
            flexShrink: 0,
            background: '#fff',
            borderBottom: '1px solid var(--border-static)',
            display: 'flex',
            alignItems: 'center',
            padding: '0 24px',
            gap: 16,
          }}
        >
          {trusted ? (
            <button
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 9,
                height: 38,
                padding: '0 12px',
                borderRadius: 8,
                border: '1px solid var(--border-static)',
                background: '#fff',
                cursor: 'pointer',
              }}
            >
              <Avatar initials={biz.initials} color={biz.color} size={22} />
              <span style={{ fontSize: 14, fontWeight: 600 }}>{biz.name}</span>
              <Icon name="chevron-down" size={14} color="var(--text-secondary)" />
            </button>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <Avatar initials="BI" color="var(--decorative-2)" size={26} />
              <span style={{ fontSize: 14, fontWeight: 600 }}>Berko Insurance Inc</span>
            </div>
          )}
          <div style={{ flex: 1 }} />
          <IconButton name="help-circle" ariaLabel="Help" />
          <IconButton name="notification" ariaLabel="Notifications" />
          <Avatar initials="DR" color="var(--decorative-4)" size={32} />
        </header>

        {app.isLive && (
          <div
            style={{
              background: 'var(--critical-100)',
              color: 'var(--critical-900)',
              padding: '8px 24px',
              fontSize: 13,
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Icon name="warning" size={15} color="var(--critical-700)" /> You're in live mode — payments here move real money.
          </div>
        )}

        {/* content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '32px 32px 60px' }}>
          <div style={{ maxWidth: 1080, margin: '0 auto' }}>
            <ApiKeyGate>
              {view === 'home' && <DashHome go={go} setView={setView} trusted={trusted} />}
              {view === 'businesses' && <BusinessesView trusted={trusted} biz={biz} setBiz={setBiz} setView={setView} />}
              {view === 'payments' && <PaymentsView />}
              {view === 'funding' && <FundingView />}
              {view === 'delivery' && <DeliveryView />}
              {view === 'keys' && <KeysView />}
              {view === 'logs' && <LogsView />}
              {view === 'events' && <EventsView />}
              {view === 'webhooks' && <WebhooksView />}
            </ApiKeyGate>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Home view ----------------------------------------------------------
interface DashHomeProps {
  go: Go;
  setView: (view: string) => void;
  trusted: boolean;
  style?: CSSProperties;
}
function DashHome({ go, setView, trusted }: DashHomeProps) {
  const app = useApp();
  const recent = DASH.payments.slice(0, 4);
  const ineligible = DASH.businesses.filter((b) => !b.eligible);
  const totalVol = DASH.businesses.reduce((a, b) => a + b.volume, 0);
  return (
    <div>
      <PageTitle
        title={`Welcome${app.companyName ? `, ${app.companyName}` : ''}`}
        sub={`Connected to ${app.envLabel}.`}
        action={
          <Button leftIcon="add" onClick={() => setView('payments')}>
            Create payment
          </Button>
        }
      />

      <PreviewNote>
        The overview metrics and recent-payments feed need aggregate / list endpoints the API doesn't expose yet.{' '}
        <strong>Businesses</strong> and <strong>Funding sources</strong> below are live against {app.envLabel}.
      </PreviewNote>

      {ineligible.length > 0 && trusted && (
        <Banner kind="warning" style={{ marginBottom: 20 }} cta="Review" onCta={() => setView('businesses')}>
          <strong>{ineligible[0].name}</strong> can't make payments yet — KYC is incomplete.{' '}
          {ineligible.length > 1 ? `${ineligible.length} businesses need attention.` : ''}
        </Banner>
      )}

      <div className="grid-4" style={{ marginBottom: 24 }}>
        <StatCard label="Total volume" value={fmtMoney(totalVol)} sub="Last 30 days" icon="dollar" accent />
        <StatCard label="Payments" value="42" sub="8 scheduled" icon="pay" />
        <StatCard label="In flight" value="3" sub="ACH & checks" icon="clock" />
        <StatCard label="Success rate" value="98.6%" sub="Last 30 days" icon="checked-circle-fill" />
      </div>

      <div className="grid-2" style={{ gridTemplateColumns: '1.5fr 1fr', alignItems: 'start' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>Recent payments</h3>
            <div style={{ flex: 1 }} />
            <NakedButton rightIcon="arrow-right" onClick={() => setView('payments')}>
              View all
            </NakedButton>
          </div>
          <Table
            columns={[
              { key: 'payee', label: 'Payee', render: (r) => <span style={{ fontWeight: 600 }}>{r.payee}</span> },
              {
                key: 'method',
                label: 'Method',
                render: (r) => (
                  <Pill kind="neutral" dot={false}>
                    {r.method === 'ach' ? 'ACH' : 'Check'}
                  </Pill>
                ),
              },
              {
                key: 'amount',
                label: 'Amount',
                align: 'right',
                render: (r) => (
                  <span className="om-mono" style={{ fontWeight: 600 }}>
                    {fmtMoney(r.amount)}
                  </span>
                ),
              },
              { key: 'status', label: 'Status', render: (r) => <Pill kind={statusKind(r.status)}>{statusLabel(r.status)}</Pill> },
            ]}
            rows={recent}
          />
        </div>
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Get to your first payment</h3>
          <Card padding={20}>
            <Tracker
              vertical
              current={3}
              steps={[
                { label: 'API key created', sub: 'Sandbox key active' },
                { label: 'Business eligible', sub: 'Sunrise Bakery · KYC complete' },
                { label: 'Funding source added', sub: 'Operating account ••6789' },
                { label: 'Add a delivery method', sub: 'ACH or paper check' },
                { label: 'Create a payment', sub: 'Track it end to end' },
              ]}
            />
            <Button variant="secondary" fullWidth style={{ marginTop: 8 }} onClick={() => setView('delivery')}>
              Add a delivery method
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
}

export { Drawer, Table, StatCard, PageTitle, Dashboard, DashHome };
